'use client'
import { useContext, useEffect, useRef, useState } from 'react'
import {
  DesktopHomeScrollContainerContext,
  MobileHomeScrollContainerContext,
} from '@/components/providers/Providers'
import { observeHomeCardVideo } from '@/components/home/homeCardVideoObserver'

type Props = {
  src: string
  className?: string
  /**
   * true（首屏卡片）：保持现状——立即 autoplay 全量加载。首屏视频的 onLoadedData
   * 计入首页媒体就绪指标（RUM target 只含视口内单元），绝不能加门控推迟它。
   * false（折下卡片）：进入视口前只 preload="metadata"（几 KB 拿时长/首帧信息），
   * 提前半个视口高度激活为 autoplay 全量加载——用户滚到时已在播，体感无差，
   * 但首屏阶段不再与首屏媒体抢带宽（现状是折下视频也 autoplay 全量下载）。
   */
  eager?: boolean
  onLoadedData?: () => void
  onError?: () => void
}

/**
 * 首页卡片视频：按 eager 分「首屏立即播」与「折下视口门控」两种模式的统一实现。
 * 供双端 4 个卡片组件（Character/Story × desktop/mobile）复用，替代裸 <video>。
 * 注意 autoplay 属性会令浏览器无视 preload 全量下载，所以门控态不能带 autoPlay，
 * 激活时再置 autoPlay 并显式 play()（mount 后改属性不会自动触发播放）。
 */
export default function HomeCardVideo({
  src,
  className,
  eager = false,
  onLoadedData,
  onError,
}: Props) {
  const videoRef = useRef<HTMLVideoElement>(null)
  const desktopScrollContainerRef = useContext(
    DesktopHomeScrollContainerContext
  )
  const mobileScrollContainerRef = useContext(MobileHomeScrollContainerContext)
  // 激活为单向闩：一旦 true 不再回退（滚离视口不暂停下载/播放，维持现状行为）。
  const [activated, setActivated] = useState(eager)

  useEffect(() => {
    if (eager) setActivated(true)
  }, [eager])

  useEffect(() => {
    if (activated) return
    const el = videoRef.current
    if (!el) return
    // 环境不支持 IO 时直接激活，退化为现状（绝不让视频永远不播）。
    if (typeof IntersectionObserver === 'undefined') {
      setActivated(true)
      return
    }
    // 两端首页都在内层 overflow 容器滚动。必须把该容器设为 root；默认 viewport
    // 会被中间滚动容器裁剪，rootMargin 无法让折下视频提前相交。
    const scrollRoot =
      mobileScrollContainerRef?.current ?? desktopScrollContainerRef?.current
    // ref 异常缺失时恢复原来的立即播放，功能正确性优先于门控收益。
    if (!scrollRoot) {
      setActivated(true)
      return
    }
    return observeHomeCardVideo(el, scrollRoot, () => setActivated(true))
  }, [activated, desktopScrollContainerRef, mobileScrollContainerRef])

  useEffect(() => {
    if (!activated) return
    const el = videoRef.current
    if (!el) return
    // 自动播放被策略拒绝时静默（muted 下几乎不会发生）。
    el.play().catch(() => {})
  }, [activated])

  return (
    <video
      ref={videoRef}
      src={src}
      autoPlay={activated}
      preload={activated ? 'auto' : 'metadata'}
      loop
      muted
      playsInline
      onLoadedData={onLoadedData}
      onError={onError}
      className={className}
    />
  )
}
